import { useEffect, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Checkbox, Grid, FormControlLabel, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { AppointmentDetailsDto } from "../../models/appointmentDetailsDto";
import { AppointmentServiceInternal } from "../../services/appointment.service";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";

interface AppointmentDetailsModalProps {
  open: boolean;
  onClose: () => void;
  handleModifyDetails: (data: any) => Promise<void>;
  appointmentDetails: AppointmentDetailsDto;
}

interface CountyOption {
  countyid: number;
  countyname: string;
}

const toDateInputValue = (value: Date | string | null | undefined): string => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.substring(0, 10);
  }
  const parsed = new Date(value);
  if (isNaN(parsed.getTime())) return "";
  return `${parsed.getFullYear()}-${(parsed.getMonth() + 1).toString().padStart(2, "0")}-${parsed.getDate().toString().padStart(2, "0")}`;
};

const AppointmentDetailsModal = ({
  open,
  onClose,
  handleModifyDetails,
  appointmentDetails,
}: AppointmentDetailsModalProps) => {
  const [heldForAnotherCounty, setHeldForAnotherCounty] = useState<boolean | null | undefined>(false);
  const [countyOptions, setCountyOptions] = useState<CountyOption[]>([]);
  const [selectedCountyName, setSelectedCountyName] = useState<string | undefined>("");

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      inmateFirstName: "",
      inmateLastName: "",
      inmatedob: "",
      heldforanothercounty: false,
      countyid: "",
      additionalinfo: "",
      medications: "",
    }
  });

  useEffect(() => {
    if (open) {
      if (countyOptions.length === 0) {
        fetchCountyOptions();
      } else {
        setValue("inmateFirstName", appointmentDetails?.inmateFirstName ?? "");
        setValue("inmateLastName", appointmentDetails?.inmateLastName ?? "");
        setValue("inmatedob", toDateInputValue(appointmentDetails?.inmatedob));
        setHeldForAnotherCounty(appointmentDetails?.heldforanothercounty ?? false);
        setValue("heldforanothercounty", appointmentDetails?.heldforanothercounty ?? false);
        setValue("countyid", appointmentDetails?.countyid?.toString() === "0" || !appointmentDetails?.countyid ? "" : appointmentDetails?.countyid?.toString());
        setSelectedCountyName(appointmentDetails?.countyname ?? "");
        setValue("medications", appointmentDetails?.medications ?? "");
        setValue("additionalinfo", appointmentDetails?.additionalinfo ?? "");
      }
    }
  }, [open, appointmentDetails, countyOptions, setValue]);

  const fetchCountyOptions = async () => {
    try {
      const response = await AppointmentServiceInternal.county();
      if (response?.data.isSuccess) {
        setCountyOptions(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching counties:", error);
    }
  };

  const onHeldForAnotherCountyChange  = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setValue("countyid", appointmentDetails?.countyid?.toString() === "0" || !appointmentDetails?.countyid ? "" : appointmentDetails?.countyid?.toString());
    } else {
      setValue("countyid", "");
      setSelectedCountyName("");
    }
    setValue("heldforanothercounty", isChecked);
    setHeldForAnotherCounty(isChecked);
  };

  const onCountyChange  = (event: React.ChangeEvent<HTMLInputElement>) => {
    const countyId = Number(event.target.value);
    const countyName = countyOptions.find(c => c.countyid === countyId)?.countyname;
    console.log(countyName);
    setValue("countyid", event.target.value);
    setSelectedCountyName(countyName);
  };

  const onSubmit = async (data: any) => {
    const inmateFirstName = (data.inmateFirstName ?? "").trim();
    const inmateLastName = (data.inmateLastName ?? "").trim();
    const inmatedob = data.inmatedob;
    if (
      inmateFirstName === (appointmentDetails.inmateFirstName ?? "") &&
      inmateLastName === (appointmentDetails.inmateLastName ?? "") &&
      inmatedob === toDateInputValue(appointmentDetails.inmatedob) &&
      appointmentDetails.heldforanothercounty === data.heldforanothercounty &&
      appointmentDetails.countyid === Number(data.countyid) &&
      appointmentDetails.additionalinfo === data.additionalinfo &&
      appointmentDetails.medications === data.medications
    ) {
      toast.warn("No updates to submit. All fields remained unchanged.");
    } else if(data.heldforanothercounty && (data.countyid === "0" || data.countyid === "")) {
      toast.warn("You must select a county from the list.");
    } else {
      let details: AppointmentDetailsDto = {
        athenaAppointmentId: appointmentDetails.athenaAppointmentId,
        inmateFirstName: inmateFirstName,
        inmateLastName: inmateLastName,
        inmatedob: inmatedob,
        heldforanothercounty: data.heldforanothercounty,
        countyid: Number(data.countyid),
        countyname: selectedCountyName,
        additionalinfo: data.additionalinfo,
        medications: data.medications
      }
      await handleModifyDetails(details);
    }
  }

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>Update Inmate Information</DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="inmateFirstName"
                  control={control}
                  rules={{ validate: (value) => (value ?? "").trim() !== "" || "First name is required" }}
                  render={({ field }) => (
                    <>
                      <InputLabel>First Name:</InputLabel>
                      <TextField
                        {...field}
                        variant="outlined"
                        margin="normal"
                        type="text"
                        size="small"
                        fullWidth
                        error={!!errors.inmateFirstName}
                        helperText={errors.inmateFirstName?.message}
                      />
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="inmateLastName"
                  control={control}
                  rules={{ validate: (value) => (value ?? "").trim() !== "" || "Last name is required" }}
                  render={({ field }) => (
                    <>
                      <InputLabel>Last Name:</InputLabel>
                      <TextField
                        {...field}
                        variant="outlined"
                        margin="normal"
                        type="text"
                        size="small"
                        fullWidth
                        error={!!errors.inmateLastName}
                        helperText={errors.inmateLastName?.message}
                      />
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="inmatedob"
                  control={control}
                  rules={{ required: "Date of birth is required" }}
                  render={({ field }) => (
                    <>
                      <InputLabel>Date of Birth:</InputLabel>
                      <TextField
                        {...field}
                        variant="outlined"
                        margin="normal"
                        type="date"
                        size="small"
                        fullWidth
                        error={!!errors.inmatedob}
                        helperText={errors.inmatedob?.message}
                      />
                    </>
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={6}>
                <Controller
                  name="heldforanothercounty"
                  control={control}
                  defaultValue={appointmentDetails?.heldforanothercounty ?? false}
                  render={({ field }) => (
                    <FormControlLabel
                      label="Held for another county?"
                      control={
                        <Checkbox
                          { ...field }
                          checked={field.value}
                          onChange={onHeldForAnotherCountyChange}
                          size="small"
                        />
                      }
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <div style={{visibility: heldForAnotherCounty ? 'visible' : 'hidden'}}>
                  <Controller
                    name="countyid"
                    control={control}
                    defaultValue={appointmentDetails?.countyid?.toString()}
                    render={({ field }) => (
                      <FormControl fullWidth size="small" margin="normal">
                        <InputLabel id="countyLabel">County</InputLabel>
                        <Select
                          {...field}
                          labelId="countyLabel"
                          label="County"
                          size="small"
                          onChange={(e) => {
                            field.onChange(e);
                            onCountyChange(
                              e as React.ChangeEvent<HTMLInputElement>
                            );
                          }}
                        >
                          <MenuItem value="" disabled>
                            Select...
                          </MenuItem>
                          {countyOptions.map((option: CountyOption) => (
                            <MenuItem
                              key={option.countyid}
                              value={option.countyid}
                            >
                              {option.countyname}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </div>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Controller
                  name="medications"
                  control={control}
                  defaultValue={appointmentDetails?.medications ?? ""}
                  render={({ field }) => (
                    <>
                      <InputLabel>Medications:</InputLabel>
                      <TextField
                        {...field}
                        variant="outlined"
                        margin="normal"
                        type="text"
                        rows={3}
                        multiline
                        className="m-auto"
                        fullWidth
                      />
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="additionalinfo"
                  control={control}
                  defaultValue={appointmentDetails?.additionalinfo ?? ""}
                  render={({ field }) => (
                    <>
                      <InputLabel>Additional Info:</InputLabel>
                      <TextField
                        {...field}
                        variant="outlined"
                        margin="normal"
                        type="text"
                        rows={3}
                        multiline
                        className="m-auto"
                        fullWidth
                      />
                    </>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{mb: 1}}>
            <Button type="submit" color="primary" variant="contained">
              Update
            </Button>
            <Button onClick={onClose} color="error" variant="outlined">
              Close
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
};

export default AppointmentDetailsModal;